import Axios, { AxiosHeaders, AxiosRequestHeaders, isAxiosError } from 'axios'
import { api } from 'src/configuration'

import {
  RestApiResponse,
  RestApiTransformer,
  RestRequest,
} from '../helpers/RestHelpers'

class RestApiClient {
  static async call<Request extends RestRequest>(
    request: Request,
    headers?: AxiosHeaders
  ): Promise<RestApiResponse<Request>> {
    let result: RestApiResponse
    const { transformer, ...config } = request
    try {
      result = await Axios({
        ...config,
        headers: {
          // Strapi v5 compat: keep the v4 response shape (data.attributes + numeric id)
          // by default. Individual requests opt out by setting this header to
          // `undefined` in their own headers as they migrate to the flat v5 shape.
          'Strapi-Response-Format': 'v4',
          ...config?.headers,
          ...headers,
          ...(api.REST_API_TOKEN && {
            Authorization: `Bearer ${api.REST_API_TOKEN}`,
          }),
        },
      })
      if ('error' in result) {
        throw new Error(`An error ocurred ${result.error}`)
      }
    } catch (e) {
      if (isAxiosError(e) && e.response) {
        console.error(
          `RestApi Error [${e.response.status}] ${e.config?.url}:`,
          JSON.stringify(e.response.data?.error ?? e.response.data, null, 2)
        )
        return { ...e.response, errors: e?.response?.data?.error }
      }
      console.error(`RestApi Error:`, e)
      return {
        data: undefined,
        errors: (e as Error)?.message ?? 'error',
        status: 500,
        statusText: (e as Error)?.message ?? 'error',
        headers: {
          ...config?.headers,
          ...headers,
        },
        config: {
          ...config,
          headers: config.headers as AxiosRequestHeaders,
        },
      }
    }

    const data = await RestApiClient.transform(result.data, transformer)

    return { ...result, data }
  }

  static async transform(
    data: RestApiResponse['data'],
    transformer: RestApiTransformer<Transformer>
  ) {
    if (!data || !transformer) {
      return data
    }

    return await transformer(data)
  }
}

export default RestApiClient
